/*
    Recorte real de imagen para react-easy-crop.

    croppedAreaPixels viene expresado sobre el bounding box de la imagen
    ya rotada. Primero dibujamos esa imagen completa en un canvas con sus
    dimensiones reales y recien despues extraemos el rectangulo elegido.
    Esto evita el desplazamiento que aparecia al redimensionar el canvas
    antes de aplicar x/y del recorte.
*/

const onLoadImage = (src) => new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', reject);
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = src;
});

const toRadians = (degrees = 0) => (Number(degrees || 0) * Math.PI) / 180;

const rotatedSize = (width, height, rotation = 0) => {
    const angle = toRadians(rotation);
    return {
        width: Math.abs(Math.cos(angle) * width) + Math.abs(Math.sin(angle) * height),
        height: Math.abs(Math.sin(angle) * width) + Math.abs(Math.cos(angle) * height),
    };
};

const clamp = (value, min, max) => Math.min(Math.max(Number(value || 0), min), max);

/*
    area      -> { x, y, width, height } de onCropComplete
    rotation  -> grados
    maxWidth  -> ancho maximo del archivo final
    output    -> 'blob' | 'dataURL' | 'file'
*/
export const onCropImage = async (src, area, {
    rotation = 0,
    maxWidth = 1200,
    quality = 0.9,
    mimeType = 'image/jpeg',
    output = 'file',
    fileName = 'recorte.jpg',
} = {}) => {
    if (!src || !area?.width || !area?.height) throw new Error('Recorte invalido');

    const image = await onLoadImage(src);
    const imageWidth = image.naturalWidth || image.width;
    const imageHeight = image.naturalHeight || image.height;
    const angle = toRadians(rotation);
    const bounds = rotatedSize(imageWidth, imageHeight, rotation);

    const sourceCanvas = document.createElement('canvas');
    sourceCanvas.width = Math.max(1, Math.ceil(bounds.width));
    sourceCanvas.height = Math.max(1, Math.ceil(bounds.height));

    const sourceCtx = sourceCanvas.getContext('2d');
    if (!sourceCtx) throw new Error('Canvas no disponible');

    sourceCtx.imageSmoothingEnabled = true;
    sourceCtx.imageSmoothingQuality = 'high';
    sourceCtx.translate(sourceCanvas.width / 2, sourceCanvas.height / 2);
    sourceCtx.rotate(angle);
    sourceCtx.translate(-imageWidth / 2, -imageHeight / 2);
    sourceCtx.drawImage(image, 0, 0, imageWidth, imageHeight);

    // react-easy-crop puede entregar decimales y, por redondeo, algun borde
    // puede quedar un pixel fuera del canvas. Lo normalizamos antes de dibujar.
    const sx = Math.round(clamp(area.x, 0, Math.max(0, sourceCanvas.width - 1)));
    const sy = Math.round(clamp(area.y, 0, Math.max(0, sourceCanvas.height - 1)));
    const sw = Math.max(1, Math.round(clamp(area.width, 1, sourceCanvas.width - sx)));
    const sh = Math.max(1, Math.round(clamp(area.height, 1, sourceCanvas.height - sy)));

    const scale = sw > Number(maxWidth || sw) ? Number(maxWidth) / sw : 1;
    const outputWidth = Math.max(1, Math.round(sw * scale));
    const outputHeight = Math.max(1, Math.round(sh * scale));

    const cropCanvas = document.createElement('canvas');
    cropCanvas.width = outputWidth;
    cropCanvas.height = outputHeight;
    const cropCtx = cropCanvas.getContext('2d');
    if (!cropCtx) throw new Error('Canvas no disponible');

    cropCtx.imageSmoothingEnabled = true;
    cropCtx.imageSmoothingQuality = 'high';
    cropCtx.drawImage(
        sourceCanvas,
        sx, sy, sw, sh,
        0, 0, outputWidth, outputHeight,
    );

    if (output === 'dataURL') return cropCanvas.toDataURL(mimeType, quality);

    return new Promise((resolve, reject) => {
        cropCanvas.toBlob((blob) => {
            if (!blob) {
                reject(new Error('No se pudo generar la imagen recortada'));
                return;
            }
            if (output === 'blob') {
                resolve(blob);
                return;
            }
            resolve(new File([blob], fileName, { type: mimeType }));
        }, mimeType, quality);
    });
};

export const CROP_ASPECTS = [
    { value: 1, label: '1:1 — Cuadrada' },
    { value: 4 / 3, label: '4:3 — Clásica' },
    { value: 16 / 9, label: '16:9 — Panorámica' },
    { value: 3 / 4, label: '3:4 — Vertical' },
    { value: 9 / 16, label: '9:16 — Historia' },
];
